/**
 * Hook for fetching and managing sync job status with auto-refresh
 */

import { useState, useEffect, useCallback } from 'react';
import type { SyncJob, SyncSource } from '../types/sync';
import { fetchSyncStatus, startSync } from '../lib/management-api';
import { useAuth } from '../components/Auth';
import CONFIG from '../config';

// Auto-refresh interval in milliseconds
const AUTO_REFRESH_INTERVAL = 5000;

// How long to protect optimistic updates from being overwritten by stale poll data (ms)
const OPTIMISTIC_PROTECTION_DURATION = 10000;

// Internal type that tracks optimistic update timestamp on each sync
type SyncJobWithOptimistic = SyncJob & { _optimisticUntil?: number };

interface UseSyncJobsResult {
  syncs: SyncJob[];
  isLoading: boolean;
  error: string | null;
  startSync: (source: SyncSource) => Promise<void>;
  refetch: () => void;
  lastUpdated: Date | null;
}

/**
 * Hook to fetch and manage sync job status with 5-second polling
 *
 * @returns Object with sync jobs data, loading state, error, and control functions
 */
export function useSyncJobs(): UseSyncJobsResult {
  const { getAuthHeader, isAuthenticated } = useAuth();
  const [syncs, setSyncs] = useState<SyncJobWithOptimistic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const refetch = useCallback(() => {
    setFetchTrigger((prev) => prev + 1);
  }, []);

  // Main fetch effect
  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      setError('Authentication required');
      return;
    }

    const abortController = new AbortController();

    async function loadSyncStatus() {
      setIsLoading(true);
      setError(null);

      try {
        const authHeader = getAuthHeader();
        if (!authHeader && !CONFIG.DEV_MODE) {
          throw new Error('No authentication token available');
        }

        const headers: Record<string, string> = authHeader
          ? { Authorization: authHeader }
          : {};
        const response = await fetchSyncStatus(headers, abortController.signal);

        setSyncs(response.syncs);
        setLastUpdated(new Date());
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        setError(
          err instanceof Error ? err.message : 'Failed to fetch sync status',
        );
      } finally {
        setIsLoading(false);
      }
    }

    loadSyncStatus();

    return () => {
      abortController.abort();
    };
  }, [isAuthenticated, getAuthHeader, fetchTrigger]);

  // Auto-refresh polling effect (always enabled)
  useEffect(() => {
    if (!isAuthenticated || isLoading) {
      return;
    }

    const intervalId = setInterval(async () => {
      try {
        const authHeader = getAuthHeader();
        if (!authHeader && !CONFIG.DEV_MODE) return;

        const headers: Record<string, string> = authHeader
          ? { Authorization: authHeader }
          : {};
        const response = await fetchSyncStatus(headers);
        const now = Date.now();

        // Merge backend data with optimistic updates
        // Protect optimistically-updated sources from stale backend data
        setSyncs((prevSyncs) =>
          response.syncs.map((backendSync) => {
            const prevSync = prevSyncs.find(
              (s) => s.source === backendSync.source,
            );
            const optimisticUntil = prevSync?._optimisticUntil;

            // If we have an optimistic update for this source that hasn't expired
            if (optimisticUntil && now < optimisticUntil) {
              // If backend confirms processing, or shows a NEW terminal state
              // (started after our optimistic update), use backend data
              if (backendSync.status === 'processing') {
                // Backend caught up - use backend data, clear protection
                return backendSync;
              }

              // Backend still shows old status - keep our optimistic update
              if (prevSync) {
                return prevSync;
              }
            }

            // No optimistic protection or it expired - use backend data
            return backendSync;
          }),
        );
        setLastUpdated(new Date());
      } catch {
        // Silently ignore polling errors to avoid UI disruption
      }
    }, AUTO_REFRESH_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [isAuthenticated, getAuthHeader, isLoading]);

  const handleStartSync = useCallback(
    async (source: SyncSource): Promise<void> => {
      const authHeader = getAuthHeader();
      if (!authHeader && !CONFIG.DEV_MODE) {
        throw new Error('Not authenticated');
      }

      const headers: Record<string, string> = authHeader
        ? { Authorization: authHeader }
        : {};
      await startSync(source, headers);

      // Optimistic update: immediately show "processing" status
      // Include protection timestamp to prevent stale poll data from overwriting
      setSyncs((prevSyncs) =>
        prevSyncs.map((sync) =>
          sync.source === source
            ? {
                ...sync,
                status: 'processing' as const,
                startedAt: new Date().toISOString(),
                errorMessage: null,
                itemsProcessed: 0,
                itemsSynced: 0,
                itemsSkipped: 0,
                itemsFailed: 0,
                _optimisticUntil: Date.now() + OPTIMISTIC_PROTECTION_DURATION,
              }
            : sync,
        ),
      );
    },
    [getAuthHeader],
  );

  return {
    syncs,
    isLoading,
    error,
    startSync: handleStartSync,
    refetch,
    lastUpdated,
  };
}
